import { motion } from "framer-motion";

const REASONS = [
  {
    h: "The senior you meet ships the work",
    p: "Same operator you sign with is the one shipping ads at 11pm before your launch. No junior handoffs, no revolving door.",
  },
  {
    h: "Contribution margin, not clout",
    p: "We optimise the P&L line, not the engagement rate slide. If it doesn't move revenue, we don't pitch it.",
  },
  {
    h: "Six disciplines, one Slack",
    p: "Ads, SEO, CRO, social, web, app — all under one channel. No agency finger-pointing when the number dips on a Monday.",
  },
  {
    h: "90 days or you walk",
    p: "Quarter-to-quarter after the first sprint. We earn every renewal. Nobody at Adinvo is comfortable with a 12-month lock-in.",
  },
  {
    h: "Flat fee. Skin in the game",
    p: "Not paid a % of your ad spend — so we’ll happily tell you to cut budget the week it’s the right call.",
  },
  {
    h: "Mumbai-built. Globally shipped",
    p: "Studio in Andheri, clients across UK, US, UAE and India. Time zones are a solved problem.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why"
      data-testid="why-us"
      className="relative border-t border-white/10 bg-black py-24 md:py-32"
    >
      <div className="mx-auto max-w-[90rem] px-6 md:px-12">
        <div className="grid gap-12 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-white/65">
              / Why founders pick Adinvo
            </p>
            <h2 className="mt-5 max-w-3xl font-display text-4xl font-extrabold leading-[0.95] tracking-tighter text-white md:text-6xl">
              Six answers to{" "}
              <span className="font-serif-emph font-normal">why us</span>{" "}
              over a 30-person agency.
            </h2>
          </div>
          <p className="max-w-sm text-white/75 md:col-span-4">
            Take a scroll. If any of the six sounds like something your current
            agency doesn’t do — you know what to do next.
          </p>
        </div>

        {/* Editorial image band */}
        <div className="mt-16 grid gap-4 md:grid-cols-12">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 md:col-span-8">
            <div className="relative h-[320px] w-full md:h-[440px]">
              <img
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
                alt="Adinvo team at work"
                className="h-full w-full object-cover grayscale contrast-125 opacity-85"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/25 to-black/45" />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/85">/ Andheri Studio</span>
                <p className="mt-2 font-display text-2xl font-bold tracking-tight text-white md:text-3xl">
                  Senior operators. Zero handoffs.
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-rows-2 gap-4 md:col-span-4 md:h-[440px]">
            <div className="relative overflow-hidden rounded-2xl border border-white/10">
              <img
                src="https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Dashboards"
                className="h-full w-full object-cover grayscale contrast-125 opacity-85"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <span className="absolute bottom-4 left-4 font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white">/ P&amp;L dashboards</span>
            </div>
            <div className="flex flex-col justify-between rounded-2xl border border-white/10 bg-white/[0.03] p-6">
              <span className="font-mono text-[0.65rem] uppercase tracking-[0.2em] text-white/70">/ Since 2020</span>
              <div>
                <div className="font-display text-4xl font-extrabold tracking-tighter text-white">200+</div>
                <p className="mt-1 text-sm text-white/75">brands shipped across India, UK, US &amp; UAE.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3">
          {REASONS.map((r, i) => (
            <motion.div
              key={r.h}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`why-${i}`}
              className="bg-black p-8"
            >
              <span className="font-mono text-xs text-white/75">0{i + 1}</span>
              <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-white">
                {r.h}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-white/75">{r.p}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
